import React from 'react';
import './PrintableClientReport.css'; // Assuming a similar CSS file structure
import { format } from 'date-fns';

// Interfaces based on data structure from report.controller.js getClientReport
interface PreferredStructure {
  id: number;
  code: string;
  type: string;
  location: string;
  count: number;
}

interface ClientReportItem {
  id: number;
  name: string;
  director?: string | null;
  phone?: string | null;
  email?: string | null;
  type?: string | null;
  referralSource?: string | null;
  discount: number;
  totalSpend: number;
  totalPaid: number;
  outstandingBalance: number;
  orderCount: number;
  averageOrderValue: number;
  firstOrderDate?: Date | string | null;
  lastOrderDate?: Date | string | null;
  preferredStructures: PreferredStructure[];
  averageDiscount: number;
}

interface ClientReportData {
  summary: {
    totalClients: number;
    totalSpend: number;
    totalOutstanding: number;
    averageSpend: number;
  };
  topClientsBySpend: ClientReportItem[]; // Assuming top 5
  topClientsByOrderCount: ClientReportItem[]; // Assuming top 5
  clientTypeDistribution: Array<{ type: string; count: number; totalSpend: number }>;
  clients: ClientReportItem[];
  startDate: string; // Need to pass these from the print page component
  endDate: string;   // Need to pass these from the print page component
}

interface PrintableClientReportProps {
  reportData: ClientReportData;
}

// Helper to format dates
const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'dd.MM.yyyy');
  } catch (e) {
    return 'Invalid Date';
  }
};

// Helper to format currency
const formatCurrency = (value: number | null | undefined): string => {
  if (value === undefined || value === null) return '0 ₸';
  return new Intl.NumberFormat('ru-KZ', {
    style: 'currency',
    currency: 'KZT',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Helper for client type label
function getClientTypeLabel(type: string | null | undefined): string {
  const types: { [key: string]: string } = {
    'corporate': 'Корпоративный',
    'individual': 'Частное лицо',
    'government': 'Государственный',
    'other': 'Другой'
  };
  return type ? (types[type] || 'Не указан') : 'Не указан';
}


const PrintableClientReport: React.FC<PrintableClientReportProps> = ({ reportData }) => {
  if (!reportData) {
    return <div className="printable-container">Ошибка: Данные для отчета по клиентам не найдены.</div>;
  }

  const {
    summary,
    clients,
    startDate,
    endDate,
  } = reportData;

  return (
    <div className="printable-container client-report-container">
      <header className="printable-header">
        <h1>Отчет по клиентам</h1>
        <p>Период: {formatDate(startDate)} - {formatDate(endDate)}</p>
      </header>

      <section className="summary-section">
        <h2>Сводка по периоду</h2>
        <div className="summary-grid">
          <div><strong>Всего активных клиентов:</strong> {summary.totalClients}</div>
          <div><strong>Общая сумма заказов:</strong> {formatCurrency(summary.totalSpend)}</div>
          <div><strong>Общая задолженность:</strong> {formatCurrency(summary.totalOutstanding)}</div>
          <div><strong>Средний чек:</strong> {formatCurrency(summary.averageSpend)}</div>
        </div>
      </section>

      <section className="details-table">
        <h2>Детализация по клиентам</h2>
        <table>
          <thead>
            <tr>
              <th>Клиент</th>
              <th>Тип</th>
              <th>Кол-во заказов</th>
              <th>Общая сумма (₸)</th>
              <th>Оплачено (₸)</th>
              <th>Задолженность (₸)</th>
              <th>Ср. скидка (%)</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.id}>
                <td>{client.name}</td>
                <td>{getClientTypeLabel(client.type)}</td>
                <td>{client.orderCount}</td>
                <td>{formatCurrency(client.totalSpend)}</td>
                <td>{formatCurrency(client.totalPaid)}</td>
                <td>{formatCurrency(client.outstandingBalance)}</td>
                <td>{client.averageDiscount.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Optionally display top clients or type distribution if needed */}
      {/* <section className="top-clients"> ... </section> */}

      <footer className="printable-footer">
        Отчет сформирован: {formatDate(new Date())}
      </footer>
    </div>
  );
};

export default PrintableClientReport;
